import { useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, ChevronRight, X, Upload, Trash } from 'lucide-react';
import { ExportButtons } from '@/components/ExportButtons';
import { apiFetch } from '@/lib/api';
import { useAuthContext } from '@/context/AuthContext';
import { usePaginatedList } from '@/hooks/usePaginatedList';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { SortableTh } from '@/components/SortableTh';
import { formatCents } from '@/lib/utils';
import { UNITS, isAreaUnit } from '@billflow/shared';
import type { InventoryItem, ItemCategory, Supplier, Unit } from '@billflow/shared';

interface SuppliersResponse {
  data: { suppliers: Supplier[] };
}

// ─── Types ─────────────────────────────────────────────────────────────────────

type ItemRow = InventoryItem & { low_stock: boolean };

type StatusFilter = 'active' | 'inactive' | 'all';
type CategoryFilter = 'all' | 'goods' | 'service';

// ─── Module-level constants ────────────────────────────────────────────────────

const statusOptions: { label: string; value: StatusFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

const categoryTabs: { label: string; value: CategoryFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Goods', value: 'goods' },
  { label: 'Services', value: 'service' },
];

// ─── Item Modal ────────────────────────────────────────────────────────────────

interface ItemModalProps {
  mode: 'create' | 'edit';
  item?: InventoryItem;
  onClose: () => void;
  onSaved: () => void;
}

const SELECT_CLS =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

function ItemModal({ mode, item, onClose, onSaved }: ItemModalProps) {
  const [code, setCode] = useState(item?.code ?? '');
  const [name, setName] = useState(item?.name ?? '');
  const [category, setCategory] = useState<ItemCategory>(item?.category ?? 'goods');
  const [unit, setUnit] = useState<Unit>((item?.unit as Unit) ?? 'Pcs');
  const [sellingDollars, setSellingDollars] = useState(
    item ? String(item.selling_price / 100) : '',
  );
  const [purchaseDollars, setPurchaseDollars] = useState(
    item ? String(item.purchase_price / 100) : '',
  );
  const [openingStock, setOpeningStock] = useState('0');
  const [minStock, setMinStock] = useState(String(item?.min_stock ?? 0));
  const [sqftPerBox, setSqftPerBox] = useState(
    item?.sqft_per_box != null ? String(item.sqft_per_box) : '',
  );
  const [sqftPerPiece, setSqftPerPiece] = useState(
    item?.sqft_per_piece != null ? String(item.sqft_per_piece) : '',
  );
  const [packQty, setPackQty] = useState(item?.pack_qty != null ? String(item.pack_qty) : '');
  const [packUnit, setPackUnit] = useState(item?.pack_unit ?? '');
  const [weight, setWeight] = useState(item?.weight != null ? String(item.weight) : '');
  const [dimensions, setDimensions] = useState(item?.dimensions ?? '');
  const [preferredSupplierId, setPreferredSupplierId] = useState(
    item?.preferred_supplier_id != null ? String(item.preferred_supplier_id) : '',
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sqftPerBoxError, setSqftPerBoxError] = useState('');

  // Active suppliers for the optional Preferred supplier picker.
  const { data: suppliersData } = useQuery({
    queryKey: ['suppliers-active'],
    queryFn: () => apiFetch<SuppliersResponse>('/api/suppliers?status=active'),
    staleTime: 30_000,
  });
  const suppliers = suppliersData?.data.suppliers ?? [];

  // Photo upload state (edit mode only — item must exist)
  const [imageUrl, setImageUrl] = useState<string | null>(
    item?.image_path ? `/api/inventory/${item.id}/image?t=${Date.now()}` : null,
  );
  const [photoLoading, setPhotoLoading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const isGoods = category === 'goods';
  const isSqft = isAreaUnit(unit);

  async function handlePhotoUpload(file: File) {
    if (!item?.id) return;
    setPhotoLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`/api/inventory/${item.id}/image`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? 'Upload failed');
      }
      setImageUrl(`/api/inventory/${item.id}/image?t=${Date.now()}`);
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setPhotoLoading(false);
    }
  }

  async function handlePhotoDelete() {
    if (!item?.id) return;
    setPhotoLoading(true);
    try {
      const res = await fetch(`/api/inventory/${item.id}/image`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? 'Delete failed');
      }
      setImageUrl(null);
      toast.success('Photo removed');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setPhotoLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSqftPerBoxError('');

    // Client-side: sqft_per_box required when unit = Sq.ft.
    if (isSqft && isGoods && (!sqftPerBox.trim() || parseFloat(sqftPerBox) <= 0)) {
      setSqftPerBoxError('Required for Sq.ft. items and must be > 0');
      return;
    }

    setLoading(true);

    try {
      const body: Record<string, unknown> = {
        code: code.trim() || undefined,
        name: name.trim(),
        // category is immutable after creation — only send on create
        ...(mode === 'create' ? { category } : {}),
        unit,
        selling_price: Math.round(parseFloat(sellingDollars) * 100),
        purchase_price: Math.round(parseFloat(purchaseDollars) * 100),
        weight: weight.trim() ? parseFloat(weight) : null,
        dimensions: dimensions.trim() || null,
        // Optional preferred supplier — a number when chosen, null to clear.
        preferred_supplier_id: preferredSupplierId ? parseInt(preferredSupplierId, 10) : null,
      };

      if (isGoods) {
        body.min_stock = parseInt(minStock, 10) || 0;

        if (isSqft) {
          body.sqft_per_box = parseFloat(sqftPerBox);
          body.sqft_per_piece = sqftPerPiece.trim() ? parseFloat(sqftPerPiece) : null;
          body.pack_qty = null;
          body.pack_unit = null;
        } else {
          // Omit sqft_per_box rather than sending null: the PATCH endpoint only runs
          // the Sq.ft. box-size validation when the body includes sqft_per_box, so
          // omitting it lets a non-Sq.ft. edit succeed without tripping that rule.
          body.sqft_per_piece = null;
          body.pack_qty = packQty.trim() ? parseFloat(packQty) : null;
          body.pack_unit = packUnit.trim() || null;
        }
      }

      if (mode === 'create' && isGoods) {
        body.stock_qty = parseInt(openingStock, 10) || 0;
      }

      if (mode === 'create') {
        await apiFetch('/api/inventory', { method: 'POST', body: JSON.stringify(body) });
        toast.success('Item created');
      } else {
        await apiFetch(`/api/inventory/${item!.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
        toast.success('Item updated');
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-lg border bg-background shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4 sticky top-0 bg-background z-10">
          <h2 className="text-base font-semibold">
            {mode === 'create' ? 'New Item' : 'Edit Item'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Row: Code + Name */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="i-code">Code</Label>
              <Input
                id="i-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Auto-generated if blank"
                disabled={loading}
                autoComplete="off"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="i-name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="i-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Row: Category + UOM */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="i-category">
                Category <span className="text-destructive">*</span>
              </Label>
              <select
                id="i-category"
                value={category}
                onChange={(e) => setCategory(e.target.value as ItemCategory)}
                disabled={loading || mode === 'edit'}
                className={SELECT_CLS}
              >
                <option value="goods">Goods</option>
                <option value="service">Service</option>
              </select>
              {mode === 'edit' && (
                <p className="text-xs text-muted-foreground">
                  Category cannot be changed after creation.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="i-unit">Unit (UOM)</Label>
              <select
                id="i-unit"
                value={unit}
                onChange={(e) => {
                  setUnit(e.target.value as Unit);
                  setSqftPerBoxError('');
                }}
                disabled={loading}
                className={SELECT_CLS}
              >
                {UNITS.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Row: Selling Price + Purchase Price — label follows unit */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="i-sell">
                Selling price ($ / {unit}) <span className="text-destructive">*</span>
              </Label>
              <Input
                id="i-sell"
                type="number"
                min="0"
                step="0.01"
                value={sellingDollars}
                onChange={(e) => setSellingDollars(e.target.value)}
                placeholder="0.00"
                required
                disabled={loading}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="i-purchase">
                Purchase price ($ / {unit}) <span className="text-destructive">*</span>
              </Label>
              <Input
                id="i-purchase"
                type="number"
                min="0"
                step="0.01"
                value={purchaseDollars}
                onChange={(e) => setPurchaseDollars(e.target.value)}
                placeholder="0.00"
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Goods-only fields */}
          {isGoods && (
            <>
              {/* Opening Stock — create only */}
              {mode === 'create' && (
                <div className="space-y-1.5">
                  <Label htmlFor="i-opening">Opening Stock</Label>
                  <Input
                    id="i-opening"
                    type="number"
                    min="0"
                    step="1"
                    value={openingStock}
                    onChange={(e) => setOpeningStock(e.target.value)}
                    disabled={loading}
                  />
                </div>
              )}

              {/* Min Stock */}
              <div className="space-y-1.5">
                <Label htmlFor="i-min">Min Stock</Label>
                <Input
                  id="i-min"
                  type="number"
                  min="0"
                  step="1"
                  value={minStock}
                  onChange={(e) => setMinStock(e.target.value)}
                  disabled={loading}
                />
              </div>

              {/* Sq.ft. conditional fields */}
              {isSqft && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="i-sqft-box">
                      Sq.ft per box <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="i-sqft-box"
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={sqftPerBox}
                      onChange={(e) => {
                        setSqftPerBox(e.target.value);
                        setSqftPerBoxError('');
                      }}
                      placeholder="e.g. 15.5"
                      disabled={loading}
                    />
                    {sqftPerBoxError && (
                      <p className="text-xs text-destructive">{sqftPerBoxError}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="i-sqft-piece">Sq.ft per piece</Label>
                    <Input
                      id="i-sqft-piece"
                      type="number"
                      min="0"
                      step="0.01"
                      value={sqftPerPiece}
                      onChange={(e) => setSqftPerPiece(e.target.value)}
                      placeholder="Optional"
                      disabled={loading}
                    />
                  </div>
                </div>
              )}

              {/* Pcs / Bag: pack contents */}
              {!isSqft && (
                <div className="space-y-1.5">
                  <Label>
                    Pack contents{' '}
                    <span className="text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={packQty}
                      onChange={(e) => setPackQty(e.target.value)}
                      placeholder="Qty, e.g. 100"
                      disabled={loading}
                    />
                    <Input
                      value={packUnit}
                      onChange={(e) => setPackUnit(e.target.value)}
                      placeholder="Unit, e.g. Pcs or Lb"
                      disabled={loading}
                      maxLength={20}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Shown on estimates as a reference — does not affect price.
                  </p>
                </div>
              )}
            </>
          )}

          {/* Always-available enrichment fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="i-weight">Weight (lb)</Label>
              <Input
                id="i-weight"
                type="number"
                min="0"
                step="0.01"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder="Optional"
                disabled={loading}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="i-dimensions">Dimensions</Label>
              <Input
                id="i-dimensions"
                value={dimensions}
                onChange={(e) => setDimensions(e.target.value)}
                placeholder="e.g. 24×48 in"
                disabled={loading}
                maxLength={60}
              />
            </div>
          </div>

          {/* Preferred supplier — optional, clearable */}
          <div className="space-y-1.5">
            <Label htmlFor="i-supplier">
              Preferred supplier{' '}
              <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <select
              id="i-supplier"
              value={preferredSupplierId}
              onChange={(e) => setPreferredSupplierId(e.target.value)}
              disabled={loading}
              className={SELECT_CLS}
            >
              <option value="">— None —</option>
              {/* Keep the current supplier selectable even if it's now inactive. */}
              {item?.preferred_supplier_id != null &&
                !suppliers.some((s) => s.id === item.preferred_supplier_id) && (
                  <option value={String(item.preferred_supplier_id)}>
                    {item.preferred_supplier_name ?? `Supplier #${item.preferred_supplier_id}`}
                  </option>
                )}
              {suppliers.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.name}
                  {s.code ? ` (${s.code})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Photo upload — edit mode only */}
          <div className="space-y-1.5">
            <Label>Photo</Label>
            {mode === 'create' ? (
              <p className="text-xs text-muted-foreground rounded-md border border-dashed px-4 py-3">
                Save the item first to add a photo.
              </p>
            ) : (
              <div className="space-y-2">
                {imageUrl ? (
                  <div className="relative w-full max-w-[160px] rounded-md overflow-hidden border">
                    <img src={imageUrl} alt="Item photo" className="w-full h-auto object-cover" />
                    <button
                      type="button"
                      onClick={handlePhotoDelete}
                      disabled={photoLoading}
                      className="absolute top-1 right-1 rounded bg-black/60 p-1 text-white hover:bg-destructive transition-colors disabled:opacity-50"
                      title="Remove photo"
                    >
                      <Trash className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={photoLoading}
                    className="flex items-center gap-2 rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground hover:border-primary hover:text-foreground transition-colors disabled:opacity-50"
                  >
                    <Upload className="h-4 w-4" />
                    {photoLoading ? 'Uploading…' : 'Upload photo'}
                  </button>
                )}
                {imageUrl && (
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={photoLoading}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                  >
                    <Upload className="h-3 w-3" />
                    Replace photo
                  </button>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handlePhotoUpload(file);
                    e.target.value = '';
                  }}
                />
              </div>
            )}
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving…' : mode === 'create' ? 'Create Item' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Delete Confirm Dialog ─────────────────────────────────────────────────────

interface DeleteDialogProps {
  item: InventoryItem;
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
}

function DeleteDialog({ item, onCancel, onConfirm, loading }: DeleteDialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">Delete Item</h2>
          <button
            type="button"
            onClick={onCancel}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground">
            Delete <span className="font-semibold text-foreground">{item.name}</span>? This cannot
            be undone.
          </p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={onConfirm} disabled={loading}>
              {loading ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function InventoryPage() {
  const { user } = useAuthContext();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();

  // Default to Active so merged/retired items stay hidden; the dropdown still
  // offers Inactive / All for when you need to see them.
  const [status, setStatus] = useState<StatusFilter>('active');
  const [categoryTab, setCategoryTab] = useState<CategoryFilter>('all');
  const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null);
  const [editingItem, setEditingItem] = useState<InventoryItem | undefined>(undefined);
  const [deletingItem, setDeletingItem] = useState<InventoryItem | undefined>(undefined);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const {
    rows: items,
    total,
    page,
    setPage,
    limit,
    setLimit,
    sort,
    dir,
    toggleSort,
    q,
    setQ,
    isLoading,
  } = usePaginatedList<ItemRow>({
    endpoint: '/api/inventory',
    entityKey: 'items',
    defaultSort: 'created',
    defaultDir: 'desc',
    extraParams: {
      status: status !== 'all' ? status : undefined,
      category: categoryTab !== 'all' ? categoryTab : undefined,
    },
  });

  // Low-stock indicator reflects the current page of results.
  const lowStockCount = items.filter((i) => i.low_stock).length;

  function refetch() {
    queryClient.invalidateQueries({ queryKey: ['inventory'] });
  }

  function openCreate() {
    setEditingItem(undefined);
    setModalMode('create');
  }

  function openEdit(i: InventoryItem) {
    setEditingItem(i);
    setModalMode('edit');
  }

  async function handleDelete() {
    if (!deletingItem) return;
    setDeleteLoading(true);
    try {
      await apiFetch(`/api/inventory/${deletingItem.id}`, { method: 'DELETE' });
      toast.success('Item deleted');
      setDeletingItem(undefined);
      refetch();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete item';
      toast.error(msg);
      setDeletingItem(undefined);
    } finally {
      setDeleteLoading(false);
    }
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-bold tracking-tight">Inventory</h1>
          {!isLoading && (
            <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
              {total}
            </span>
          )}
          {!isLoading && lowStockCount > 0 && (
            <Badge variant="amber">{lowStockCount} low stock</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ExportButtons
            endpoint="/api/inventory"
            params={{
              q: q || undefined,
              status: status !== 'all' ? status : undefined,
              category: categoryTab !== 'all' ? categoryTab : undefined,
            }}
          />
          <Button size="sm" onClick={openCreate}>
            <Plus className="h-4 w-4 mr-1.5" />
            New Item
          </Button>
        </div>
      </div>

      {/* Filters — one line: search · category tabs · status select (wraps on small screens) */}
      <div className="flex flex-wrap items-center gap-3 mb-4 mt-4">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, code…"
            className="pl-9 h-9"
          />
        </div>

        {/* Category tabs */}
        <div className="flex items-center rounded-md border bg-muted/40 p-0.5 gap-0.5 shrink-0">
          {categoryTabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setCategoryTab(tab.value)}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                categoryTab === tab.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Status select */}
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
          className="h-9 shrink-0 rounded-md border border-input bg-background px-3 py-1.5 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {statusOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <SortableTh
                label="Code"
                sortKey="code"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="w-28"
              />
              <SortableTh label="Name" sortKey="name" sort={sort} dir={dir} onToggle={toggleSort} />
              <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden sm:table-cell w-28">
                Category
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden md:table-cell w-16">
                Unit
              </th>
              <SortableTh
                label="Sell Price"
                sortKey="selling"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="w-28 text-right"
              />
              <SortableTh
                label="Stock"
                sortKey="stock"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="hidden lg:table-cell w-28"
              />
              <SortableTh
                label="Status"
                sortKey="status"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="w-24"
              />
              <th className="px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {!isLoading && items.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">
                  No items found.
                </td>
              </tr>
            )}
            {items.map((item) => (
              <tr key={`item-${item.id}`} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3 font-mono text-xs">
                  <Link
                    to={`/items/${item.id}`}
                    className="text-muted-foreground hover:text-primary hover:underline"
                  >
                    {item.code ?? '—'}
                  </Link>
                </td>
                <td className="px-4 py-3 font-medium">
                  <Link to={`/items/${item.id}`} className="text-primary hover:underline">
                    {item.name}
                  </Link>
                </td>
                <td className="px-4 py-3 hidden sm:table-cell">
                  {item.category === 'goods' ? (
                    <Badge variant="blue">Goods</Badge>
                  ) : (
                    <Badge variant="gray">Service</Badge>
                  )}
                </td>
                <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">
                  {item.unit}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {formatCents(item.selling_price)}
                </td>
                <td className="px-4 py-3 hidden lg:table-cell">
                  {item.category === 'goods' ? (
                    <span className="flex items-center gap-1.5">
                      <span className="tabular-nums">{item.stock_qty}</span>
                      {item.low_stock && <Badge variant="amber">Low</Badge>}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {item.status === 'active' ? (
                    <Badge variant="green">Active</Badge>
                  ) : (
                    <Badge variant="red">Inactive</Badge>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    <Link to={`/items/${item.id}`}>
                      <Button variant="outline" size="sm" className="h-7 px-2 text-xs">
                        View
                        <ChevronRight className="h-3 w-3 ml-0.5" />
                      </Button>
                    </Link>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => openEdit(item)}
                    >
                      <Pencil className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    {isAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:border-destructive/50"
                        onClick={() => setDeletingItem(item)}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && total > 0 && (
        <Pagination
          page={page}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      )}

      {/* Modals */}
      {modalMode && (
        <ItemModal
          mode={modalMode}
          item={editingItem}
          onClose={() => {
            setModalMode(null);
            setEditingItem(undefined);
          }}
          onSaved={refetch}
        />
      )}

      {deletingItem && (
        <DeleteDialog
          item={deletingItem}
          onCancel={() => setDeletingItem(undefined)}
          onConfirm={handleDelete}
          loading={deleteLoading}
        />
      )}
    </div>
  );
}
